<script lang="ts">
  import Am5Loader from '@/Util/Am5Loader.svelte';
  import { onDestroy } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { getXertAm5, type am5Root } from '@/Util/XertAm5';
  import { xssColors } from '@/Common/constants';
  import { fmtDate } from './format';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  // training_load.time_series of a report: the athlete's Low, High and Peak training load week by week, and their total.
  export let series: { date: string; low?: number; high?: number; peak?: number }[] = [];

  const chartId = `load-progression-${Math.random().toString(36).slice(2)}`;
  let element: HTMLElement;
  let root: am5Root | undefined;

  $: if (element && series?.length) rebuild(series);
  function rebuild(...args: unknown[]) {
    void args;
    root?.dispose();
    build();
  }

  function build() {
    const { am5, am5xy, MyTheme } = getXertAm5();
    am5.addLicense('AM5C87942260');
    root = am5.Root.new(element);
    root.setThemes([MyTheme.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { paddingLeft: 0, paddingRight: 12, layout: root.verticalLayout }));
    const rows = series.map((p, i) => ({ label: i === 0 ? $_('report.overview.start') : fmtDate(p.date, 'short'), low: p.low ?? 0, high: p.high ?? 0, peak: p.peak ?? 0, total: (p.low ?? 0) + (p.high ?? 0) + (p.peak ?? 0) }));

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 24, cellStartLocation: 0.1, cellEndLocation: 0.9 });
    xRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    xRenderer.grid.template.set('forceHidden', true);
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'label', renderer: xRenderer }));
    xAxis.data.setAll(rows);
    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.grid.template.setAll({ strokeOpacity: 0.08, strokeDasharray: [3, 4] });
    yRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer, min: 0 }));
    yAxis.ghostLabel.set('minWidth', 30);

    const bars: [string, string, string][] = [[$_('report.load.low'), 'low', xssColors.xlss], [$_('report.load.high'), 'high', xssColors.xhss], [$_('report.load.peak'), 'peak', xssColors.xpss]];
    for (const [name, field, color] of bars) {
      const s = chart.series.push(am5xy.ColumnSeries.new(root, { name, xAxis, yAxis, categoryXField: 'label', valueYField: field, fill: am5.color(color), stroke: am5.color(color),
        tooltip: am5.Tooltip.new(root, { labelText: `${name}: {valueY.formatNumber('#.0')}` }) }));
      s.columns.template.setAll({ cornerRadiusTL: 2, cornerRadiusTR: 2, width: am5.percent(90) });
      s.data.setAll(rows);
    }
    const total = chart.series.push(am5xy.LineSeries.new(root, { name: $_('report.load.total'), xAxis, yAxis, categoryXField: 'label', valueYField: 'total', stroke: am5.color('#111827'),
      tooltip: am5.Tooltip.new(root, { labelText: `${$_('report.load.total')}: {valueY.formatNumber('#.0')}` }) }));
    total.strokes.template.setAll({ strokeWidth: 2 });
    total.bullets.push(() => am5.Bullet.new(root!, { sprite: am5.Circle.new(root!, { radius: 3, fill: am5.color('#111827') }) }));
    total.data.setAll(rows);

    chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }));
    const legend = chart.children.push(am5.Legend.new(root, { centerX: am5.p50, x: am5.p50, marginTop: 6 }));
    legend.labels.template.setAll({ fontSize: 12 });
    legend.data.setAll(chart.series.values);
  }
  onDestroy(() => root?.dispose());
</script>

<Am5Loader>
  <div id={chartId} bind:this={element} class="w-full h-56" />
</Am5Loader>
